import QRCode from 'qrcode';
import type { PromotionAssetViewModel } from '@dukanti/shared';

const POSTER_WIDTH = 1200;
const POSTER_HEIGHT = 1600;
const STORY_WIDTH = 1080;
const STORY_HEIGHT = 1920;

export interface PromotionAssetDownloadCopy {
  readonly ctaText: string;
  readonly brandText?: string;
}

export async function downloadPromotionPoster(
  asset: PromotionAssetViewModel,
  copy: PromotionAssetDownloadCopy = { ctaText: asset.ctaKey, brandText: 'Dukanti' },
  filename = 'dukanti-promotion-poster.png',
): Promise<void> {
  const qrDataUrl = await QRCode.toDataURL(asset.publicUrl, { margin: 1, width: 360 });
  const svg = buildPromotionPosterSvg(asset, qrDataUrl, copy);
  const dataUrl = await svgToPngDataUrl(svg, POSTER_WIDTH, POSTER_HEIGHT);
  downloadDataUrl(dataUrl, filename);
}

export function downloadPromotionStory(
  asset: PromotionAssetViewModel,
  copy: PromotionAssetDownloadCopy = { ctaText: asset.ctaKey, brandText: 'Dukanti' },
  filename = 'dukanti-promotion-story.svg',
): void {
  const svg = buildPromotionStorySvg(asset, copy);
  downloadSvgBlob(svg, filename);
}

export function buildPromotionPosterSvg(
  asset: PromotionAssetViewModel,
  qrDataUrl: string,
  copy: PromotionAssetDownloadCopy,
): string {
  const brandText = copy.brandText ?? 'Dukanti';
  const titleLines = textLines(asset.targetTitle, 24, 3);
  const subtitleLines = asset.targetSubtitle ? textLines(asset.targetSubtitle, 32, 2) : [];
  const titleSvg = titleLines
    .map(
      (line, index) =>
        `<text x="600" y="${350 + index * 78}" text-anchor="middle" font-size="68" font-weight="800" font-family="Arial, sans-serif" fill="#292524">${escapeXml(line)}</text>`,
    )
    .join('');
  const subtitleSvg = subtitleLines
    .map(
      (line, index) =>
        `<text x="600" y="${590 + index * 48}" text-anchor="middle" font-size="38" font-family="Arial, sans-serif" fill="#57534e">${escapeXml(line)}</text>`,
    )
    .join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${POSTER_WIDTH}" height="${POSTER_HEIGHT}" viewBox="0 0 ${POSTER_WIDTH} ${POSTER_HEIGHT}" direction="${asset.direction}"><rect width="1200" height="1600" fill="#f8f5ef"/><rect x="72" y="72" width="1056" height="1456" rx="48" fill="#fffaf0" stroke="#d6cbb8" stroke-width="4"/><text x="600" y="180" text-anchor="middle" font-size="44" font-weight="700" font-family="Arial, sans-serif" fill="#3f4a24">${escapeXml(asset.storeName)}</text>${titleSvg}${subtitleSvg}<rect x="392" y="705" width="416" height="416" rx="32" fill="#ffffff" stroke="#d6cbb8" stroke-width="3"/><image href="${escapeXmlAttribute(qrDataUrl)}" x="420" y="733" width="360" height="360"/><text x="600" y="1248" text-anchor="middle" font-size="42" font-weight="700" font-family="Arial, sans-serif" fill="#3f4a24">${escapeXml(copy.ctaText)}</text><text x="600" y="1332" text-anchor="middle" font-size="26" font-family="Arial, sans-serif" fill="#78716c">${escapeXml(asset.publicUrl)}</text><text x="600" y="1450" text-anchor="middle" font-size="36" font-weight="800" font-family="Arial, sans-serif" fill="#b7791f">${escapeXml(brandText)}</text></svg>`;
}

export function buildPromotionStorySvg(
  asset: PromotionAssetViewModel,
  copy: PromotionAssetDownloadCopy,
): string {
  const brandText = copy.brandText ?? 'Dukanti';
  const imageUrl = safePromotionAssetImageUrl(asset.imageUrl);
  const titleLines = textLines(asset.targetTitle, 18, 4);
  const subtitleLines = asset.targetSubtitle ? textLines(asset.targetSubtitle, 30, 3) : [];
  const titleSvg = titleLines
    .map(
      (line, index) =>
        `<text x="540" y="${1010 + index * 84}" text-anchor="middle" font-size="74" font-weight="800" font-family="Arial, sans-serif" fill="#fffaf0">${escapeXml(line)}</text>`,
    )
    .join('');
  const subtitleSvg = subtitleLines
    .map(
      (line, index) =>
        `<text x="540" y="${1360 + index * 48}" text-anchor="middle" font-size="38" font-family="Arial, sans-serif" fill="#ede7da">${escapeXml(line)}</text>`,
    )
    .join('');
  const heroSvg = imageUrl
    ? `<rect width="${STORY_WIDTH}" height="${STORY_HEIGHT}" fill="#2f3a1f"/><image href="${escapeXmlAttribute(imageUrl)}" x="0" y="0" width="${STORY_WIDTH}" height="${STORY_HEIGHT}" preserveAspectRatio="xMidYMid slice"/><rect width="${STORY_WIDTH}" height="${STORY_HEIGHT}" fill="#1f2a14" opacity="0.62"/>`
    : `<rect width="${STORY_WIDTH}" height="${STORY_HEIGHT}" fill="#2f3a1f"/><circle cx="540" cy="470" r="190" fill="#f8f5ef" opacity="0.16"/><text x="540" y="500" text-anchor="middle" font-size="72" font-weight="800" font-family="Arial, sans-serif" fill="#f8f5ef">${escapeXml(brandText)}</text>`;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${STORY_WIDTH}" height="${STORY_HEIGHT}" viewBox="0 0 ${STORY_WIDTH} ${STORY_HEIGHT}" direction="${asset.direction}">${heroSvg}<text x="540" y="220" text-anchor="middle" font-size="48" font-weight="700" font-family="Arial, sans-serif" fill="#f8f5ef">${escapeXml(asset.storeName)}</text>${titleSvg}${subtitleSvg}<rect x="170" y="1580" width="740" height="116" rx="58" fill="#f8f5ef" opacity="0.18"/><text x="540" y="1654" text-anchor="middle" font-size="42" font-weight="800" font-family="Arial, sans-serif" fill="#f8f5ef">${escapeXml(copy.ctaText)}</text><text x="540" y="1810" text-anchor="middle" font-size="32" font-weight="800" font-family="Arial, sans-serif" fill="#f2b84b">${escapeXml(brandText)}</text></svg>`;
}

export function safePromotionAssetImageUrl(imageUrl: string | null): string | null {
  if (!imageUrl) return null;
  try {
    const parsed = new URL(imageUrl, window.location.origin);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.toString();
    if (parsed.protocol === 'blob:') return imageUrl;
    if (parsed.protocol === 'data:' && imageUrl.startsWith('data:image/')) return imageUrl;
    return null;
  } catch {
    return null;
  }
}

function downloadSvgBlob(svg: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    downloadDataUrl(url, filename);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function svgToPngDataUrl(svg: string, width: number, height: number): Promise<string> {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const image = await loadImage(url);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Promotion poster canvas context unavailable');
    context.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Promotion asset image failed to load'));
    image.src = src;
  });
}

function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  link.rel = 'noopener noreferrer';
  document.body.append(link);
  link.click();
  link.remove();
}

function textLines(value: string, maxLength: number, maxLines: number): string[] {
  const words = value.trim().split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxLength && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
    if (lines.length === maxLines) break;
  }
  if (current && lines.length < maxLines) lines.push(current);
  if (lines.length === 0) return [''];
  const lastIndex = lines.length - 1;
  if (words.join(' ').length > lines.join(' ').length) {
    const lastLine = lines[lastIndex] ?? '';
    lines[lastIndex] = `${lastLine.replace(/\s+$/, '')}...`;
  }
  return lines;
}

function escapeXml(value: string): string {
  return value.replace(/[<>&"']/g, (char) => {
    const replacements: Record<string, string> = {
      '<': '&lt;',
      '>': '&gt;',
      '&': '&amp;',
      '"': '&quot;',
      "'": '&apos;',
    };
    return replacements[char] ?? char;
  });
}

function escapeXmlAttribute(value: string): string {
  return escapeXml(value);
}
